import React from "react";
import { motion } from "framer-motion";
import { Heart, Repeat } from "lucide-react";
import api from "../../lib/api";

const AVATAR_TONES = [
  "bg-forest text-cream",
  "bg-gold text-night",
  "bg-ink text-cream",
  "bg-clay text-cream",
];

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diff = Math.max(0, Date.now() - then);
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

export const RecentSupporters = () => {
  const [supporters, setSupporters] = React.useState(null);
  const [stats, setStats] = React.useState(null);

  React.useEffect(() => {
    api
      .get("/donations/recent")
      .then((r) => setSupporters(Array.isArray(r.data) ? r.data : []))
      .catch(() => setSupporters([]));
    api
      .get("/donations/stats")
      .then((r) => setStats(r.data))
      .catch(() => setStats(null));
  }, []);

  const hasStats = stats && stats.goal > 0 && stats.raised > 0;
  // Hide the whole section until we have supporters OR meaningful stats.
  if ((!supporters || supporters.length === 0) && !hasStats) return null;

  const money = (n) =>
    `$${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
  const pct = hasStats ? Math.min(100, Math.round((stats.raised / stats.goal) * 100)) : 0;

  return (
    <section className="bg-sand border-y border-parchment" data-testid="recent-supporters">
      <div className="max-w-6xl mx-auto px-6 py-24">
        <p className="uppercase tracking-[0.25em] text-xs text-gold mb-4">With Gratitude</p>
        <div className="flex flex-wrap items-end justify-between gap-4 mb-12">
          <h2 className="font-serif text-4xl md:text-5xl text-ink">Recent Supporters</h2>
          <p className="text-ink-muted max-w-md text-sm leading-relaxed">
            Ẹ ṣé púpọ̀ — thank you. These generous friends are helping keep the Yorùbá language and
            heritage alive. Your gift can appear here too.
          </p>
        </div>

        {hasStats && (
          <div
            className="mb-14 bg-forest text-cream rounded-2xl p-8 grain-overlay"
            data-testid="donation-goal-bar"
          >
            <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
              <h3 className="font-serif text-2xl md:text-3xl">
                <span className="text-gold" data-testid="goal-raised">{money(stats.raised)}</span> of{" "}
                <span data-testid="goal-target">{money(stats.goal)}</span> raised
              </h3>
              <p className="text-cream/70 text-sm">to save the Yorùbá language</p>
            </div>
            <div className="h-4 rounded-full bg-cream/15 overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                whileInView={{ width: `${pct}%` }}
                viewport={{ once: true }}
                transition={{ duration: 1.1, ease: "easeOut" }}
                className="h-full rounded-full bg-gradient-to-r from-gold to-gold-light"
                data-testid="goal-progress-fill"
              />
            </div>
            <div className="flex items-center justify-between mt-3 text-sm">
              <span className="text-gold font-semibold" data-testid="goal-percent">{pct}% funded</span>
              {stats.supporters > 0 && (
                <span className="text-cream/70" data-testid="goal-supporter-count">
                  {stats.supporters} {stats.supporters === 1 ? "supporter" : "supporters"} so far
                </span>
              )}
            </div>
          </div>
        )}

        {supporters && supporters.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {supporters.map((s, i) => (
            <motion.div
              key={`${s.first_name}-${s.created_at}-${i}`}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: (i % 6) * 0.05 }}
              data-testid={`supporter-card-${i}`}
              className="flex items-center gap-4 bg-cream border border-parchment rounded-xl px-5 py-4 hover:border-gold transition-colors"
            >
              <span
                className={`grid place-items-center w-11 h-11 rounded-full font-serif text-lg shrink-0 ${AVATAR_TONES[i % AVATAR_TONES.length]}`}
              >
                {s.first_name?.[0]?.toUpperCase() || "♡"}
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-medium text-ink truncate">
                  {s.first_name}
                  {s.amount && <span className="text-forest font-semibold"> · {s.amount}</span>}
                </p>
                <div className="flex items-center gap-2 text-xs text-ink-muted">
                  {s.monthly ? (
                    <span className="inline-flex items-center gap-1 text-forest">
                      <Repeat className="w-3 h-3" /> Monthly donor
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1">
                      <Heart className="w-3 h-3 text-clay" /> Supporter
                    </span>
                  )}
                  {timeAgo(s.created_at) && <span>· {timeAgo(s.created_at)}</span>}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
        )}
      </div>
    </section>
  );
};

export default RecentSupporters;
